import type {IntlShape, Message} from '@pinecast/i18n';
import {useIntl} from '@pinecast/i18n';
import * as React from 'react';

type TitleLayer = {message: Message; values?: Record<string, any>};

const PageTitleContext = React.createContext<{
  /**
   * The titles around a page, innermost first. Each one places the title
   * inside it as `{subtitle}`: "Analytics" inside "{subtitle} - {podcast}"
   * inside "{subtitle} - Pinecast".
   */
  stack: Array<TitleLayer>;
  /** How deep in the page a title is set. The deepest title wins. */
  depth: number;
}>({stack: [], depth: 0});

type Claim = {depth: number; order: number; title: string};

// Every title that a mounted component asks for. The document shows the one
// set deepest in the page, so a page inside a dashboard can name itself more
// precisely than the dashboard's sidebar does. Between two at the same depth,
// the one set last wins. The title goes back to the one the server sent when
// the last claim goes, so a page that sets none never shows a stale one.
const claims = new Map<object, Claim>();
let lastOrder = 0;
let serverTitle: string | null = null;

const showWinningTitle = () => {
  let winner: Claim | null = null;
  for (const claim of claims.values()) {
    if (
      !winner ||
      claim.depth > winner.depth ||
      (claim.depth === winner.depth && claim.order > winner.order)
    ) {
      winner = claim;
    }
  }
  if (winner) {
    document.title = winner.title;
  } else if (serverTitle !== null) {
    document.title = serverTitle;
    serverTitle = null;
  }
};

const claimTitle = (key: object, depth: number, title: string) => {
  if (!claims.size && serverTitle === null) {
    serverTitle = document.title;
  }
  claims.set(key, {depth, order: ++lastOrder, title});
  showWinningTitle();
  return () => {
    claims.delete(key);
    showWinningTitle();
  };
};

const formatTitle = (
  intl: IntlShape,
  stack: Array<TitleLayer>,
  title: string,
) =>
  stack.reduce(
    (subtitle, {message, values}) =>
      intl.formatMessage(message, {...values, subtitle}),
    title,
  );

/**
 * Sets the document's title to `title` inside the titles of the pages around
 * it, for as long as the component is mounted and until the title changes.
 * `null` sets none.
 */
const useDocumentTitle = (title: string | null) => {
  const intl = useIntl();
  const {depth, stack} = React.useContext(PageTitleContext);
  const [key] = React.useState(() => ({}));
  const formatted = title === null ? null : formatTitle(intl, stack, title);
  React.useEffect(
    () => (formatted === null ? undefined : claimTitle(key, depth, formatted)),
    [depth, formatted, key],
  );
};

export const usePageTitle = (
  message: Message,
  values?: Record<string, any>,
) => {
  const intl = useIntl();
  useDocumentTitle(intl.formatMessage(message, values));
};

/** `usePageTitle` for a route element that has no component of its own. */
export const DocumentTitle = ({
  message,
  values,
}: {
  message: Message;
  values?: Record<string, any>;
}) => {
  usePageTitle(message, values);
  return null;
};

/**
 * The title of one page among others, such as "Analytics", which is a title
 * only inside the name of what it belongs to. It sets nothing when no
 * `PageTitleProvider` is around it to give that name, and leaves the title to
 * the page.
 */
export const usePageSubtitle = (subtitle: string | null) => {
  const {stack} = React.useContext(PageTitleContext);
  useDocumentTitle(stack.length ? subtitle : null);
};

export const usePageTitleEffect = (
  effect: () => [Message] | [Message, Record<string, any>] | void,
  dependencies: Array<unknown>,
) => {
  const intl = useIntl();
  const returned = React.useMemo(effect, dependencies); // eslint-disable-line react-hooks/exhaustive-deps
  useDocumentTitle(
    returned ? intl.formatMessage(returned[0], returned[1]) : null,
  );
};

export const PageTitleProvider = ({
  children,
  message,
  values,
}: {
  children: React.ReactNode;
  message: Message;
  values?: Record<string, any>;
}) => {
  const {depth, stack} = React.useContext(PageTitleContext);
  return (
    <PageTitleContext.Provider
      value={{depth: depth + 1, stack: [{message, values}, ...stack]}}
    >
      {children}
    </PageTitleContext.Provider>
  );
};

/**
 * A title set inside it outranks the one that the component around it sets,
 * whichever of them sets it first.
 */
export const PageTitleScope = ({children}: {children: React.ReactNode}) => {
  const {depth, stack} = React.useContext(PageTitleContext);
  return (
    <PageTitleContext.Provider value={{depth: depth + 1, stack}}>
      {children}
    </PageTitleContext.Provider>
  );
};
